import { useForm } from '@tanstack/react-form';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { phoneValidator } from '@cold-ambulance/isomorphic';
import {
  Button,
  CardContent,
  Input,
  Label,
  ToggleGroup,
  ToggleGroupItem,
} from '@cold-ambulance/shadcn';
import {
  EmailInput,
  FieldMessage,
  FormMessage,
  PageContainer,
  PhoneInput,
  UploadField,
  errorToFormIssue,
  uploadFile,
  useAction,
  useData,
} from '@cold-ambulance/ui';

import { useUser } from '../firebase.tsx';

const CUSTOMER_PROFILE_TABS = [
  'account_info',
  'contact_info',
  'signatory_info',
  'account_documents',
] as const;
type CustomerProfileTab = (typeof CUSTOMER_PROFILE_TABS)[number];

function useTabParam() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') ?? '';
  const value: CustomerProfileTab = (
    CUSTOMER_PROFILE_TABS as readonly string[]
  ).includes(tabParam)
    ? (tabParam as CustomerProfileTab)
    : 'account_info';
  const setValue = (next: string) => {
    if (!next) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  };
  return [value, setValue] as const;
}

export default function ProfilePage() {
  const { t } = useTranslation();
  const [value, setValue] = useTabParam();

  return (
    <PageContainer title={t('profile.title')} size="lg">
      <div className="rounded-lg border px-8 lg:px-28">
        <ToggleGroup
          dir="rtl"
          type="single"
          className="mt-7 w-full space-x-4"
          value={value}
          onValueChange={setValue}
        >
          <ToggleGroupItem
            value="account_info"
            className="border-border/20 rounded-sm border"
          >
            {t('profile.generalInfo')}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="contact_info"
            className="border-border/20 rounded-sm border"
          >
            {t('profile.contactInfo')}
          </ToggleGroupItem>
          <ToggleGroupItem
            value="signatory_info"
            className="border-border/20 rounded-sm border px-4"
          >
            الشخص المفوض بالتوقيع
          </ToggleGroupItem>
          <ToggleGroupItem
            value="account_documents"
            className="border-border/20 rounded-sm border"
          >
            {t('profile.requiredDocuments')}
          </ToggleGroupItem>
        </ToggleGroup>

        <div className="mt-14 mb-7 lg:px-30">
          {value === 'account_info' && <AccountInfo />}
          {value === 'contact_info' && <ContactInfo />}
          {value === 'signatory_info' && <SignatoryInfo />}
          {value === 'account_documents' && <AccountDocuments />}
        </div>
      </div>
    </PageContainer>
  );
}

const accountInfoSchema = z.object({
  name: z.string().min(1, { message: 'اسم مقدم الخدمة مطلوب' }),
  phoneNumber: z.string().min(1, { message: 'مطلوب رقم الجوال' }),
  email: z
    .string()
    .min(1, { message: 'البريد الإلكتروني مطلوب' })
    .email({ message: 'البريد الإلكتروني غير صحيح' }),
});
const phoneNumberValidator = accountInfoSchema.shape.phoneNumber.superRefine(
  phoneValidator({ validateEmpty: false }),
);

function AccountInfo() {
  const { t } = useTranslation();
  const userQuery = useUser();
  const user = userQuery.data;

  const updateCustomerMutation = useAction('PATCH /customer/me', {
    invalidate: ['GET /customer/me'],
    onSuccess: () => toast.success(t('shared.actions.profile.accountUpdated')),
  });

  const form = useForm({
    defaultValues: {
      name: user?.name || '',
      phoneNumber: user?.contact.phoneNumber || '',
      email: user?.email || '',
    },
    validators: {
      onSubmit: accountInfoSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateCustomerMutation.mutateAsync(value);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <div className="space-y-4">
      <CardContent className="px-0">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="name"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="name">
                  {t('profile.serviceProviderName')}
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t('placeholders.companyName')}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="email"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="email">
                  {t('common.email')}
                </Label>
                <EmailInput
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="phoneNumber"
            validators={{
              onBlurAsync: phoneNumberValidator,
            }}
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="phoneNumber">
                  {t('profile.phoneNumber')}
                </Label>
                <PhoneInput
                  id="phoneNumber"
                  dir="ltr"
                  className="rtl:flex-row-reverse"
                  value={field.state.value}
                  onChange={field.handleChange}
                  onBlur={field.handleBlur}
                  international
                  defaultCountry="SA"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-4 w-full cursor-pointer"
              >
                {isSubmitting ? t('common.saving') : t('common.save')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </div>
  );
}

function ContactInfo() {
  const { t } = useTranslation();
  const userQuery = useUser();
  const user = userQuery.data;
  const updateCustomerMutation = useAction('PATCH /customer/me', {
    invalidate: ['GET /customer/me'],
    onSuccess: () => toast.success(t('shared.actions.profile.contactUpdated')),
  });

  const schema = z.object({
    firstName: z.string().min(1, { message: 'الاسم الاول مطلوب' }),
    lastName: z.string().min(1, { message: 'اسم الاخير مطلوب' }),
    jobTitle: z.string().min(1, { message: 'المسمى الوظيفي مطلوب' }),
  });

  const form = useForm({
    defaultValues: {
      firstName: user?.contact?.firstName || '',
      lastName: user?.contact?.lastName || '',
      jobTitle: user?.contact?.jobTitle || '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateCustomerMutation.mutateAsync(value);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <div className="space-y-4 p-6">
      <CardContent className="px-0">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
            <form.Field
              name="firstName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="firstName">
                    {t('profile.firstName')}
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder={t('placeholders.firstName')}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
            <form.Field
              name="lastName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="lastName">
                    {t('profile.lastName')}
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder={t('placeholders.lastName')}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
          </div>

          <form.Field
            name="jobTitle"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="jobTitle">
                  {t('profile.contactJobTitle')}
                </Label>
                <Input
                  id="jobTitle"
                  type="text"
                  placeholder={t('placeholders.projectManager')}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-4 w-full cursor-pointer"
              >
                {isSubmitting ? t('common.saving') : t('common.save')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </div>
  );
}
function SignatoryInfo() {
  const { t } = useTranslation();
  const userQuery = useUser();
  const user = userQuery.data;
  const updateCustomerMutation = useAction('PATCH /customer/me', {
    invalidate: ['GET /customer/me'],
    onSuccess: () =>
      toast.success(t('shared.actions.profile.signatoryUpdated')),
  });

  const schema = z.object({
    signatoryFirstName: z
      .string()
      .min(1, { message: 'الاسم الأول للممثل مطلوب' }),
    signatoryLastName: z
      .string()
      .min(1, { message: 'الاسم الأخير للممثل مطلوب' }),
    signatoryJobTitle: z.string().min(1, { message: 'المنصب مطلوب' }),
    signatoryEmail: z
      .string()
      .min(1, { message: 'البريد الإلكتروني للممثل مطلوب' })
      .email({ message: 'البريد الإلكتروني غير صحيح' }),
  });

  const form = useForm({
    defaultValues: {
      signatoryFirstName: user?.signatoryFirstName || '',
      signatoryLastName: user?.signatoryLastName || '',
      signatoryJobTitle: user?.signatoryJobTitle || '',
      signatoryEmail: user?.signatoryEmail || '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateCustomerMutation.mutateAsync(value);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <div className="space-y-4 p-6">
      <CardContent className="px-0">
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2">
            <form.Field
              name="signatoryFirstName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="signatoryFirstName">
                    الاسم الأول
                  </Label>
                  <Input
                    id="signatoryFirstName"
                    type="text"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
            <form.Field
              name="signatoryLastName"
              children={(field) => (
                <div className="grid w-full items-center gap-2">
                  <Label className="text-sm" htmlFor="signatoryLastName">
                    الاسم الأخير
                  </Label>
                  <Input
                    id="signatoryLastName"
                    type="text"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  <FieldMessage errors={field.state.meta.errors} />
                </div>
              )}
            />
          </div>

          <form.Field
            name="signatoryJobTitle"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="signatoryJobTitle">
                  المنصب
                </Label>
                <Input
                  id="signatoryJobTitle"
                  type="text"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="signatoryEmail"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="signatoryEmail">
                  البريد الإلكتروني
                </Label>
                <EmailInput
                  id="signatoryEmail"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-4 w-full cursor-pointer"
              >
                {isSubmitting ? t('common.saving') : t('common.save')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </div>
  );
}

const optionalFileField = z
  .instanceof(File)
  .nullable()
  .refine((file) => !file || file.size <= 10 * 1024 * 1024, {
    message: 'يجب أن يكون حجم كل ملف 10 ميغابايت أو أقل.',
  });
const requiredFileField = z
  .instanceof(File)
  .nullable()
  .refine((file): file is File => file instanceof File, {
    message: 'هذا الحقل مطلوب. يرجى إرفاق المستند المطلوب',
  })
  .refine((file) => !file || file.size <= 10 * 1024 * 1024, {
    message: 'يجب أن يكون حجم كل ملف 10 ميغابايت أو أقل.',
  });
const fileSchema = z.object({ file: optionalFileField, changed: z.boolean() });
const requiredFileSchema = z.object({
  file: requiredFileField,
  changed: z.boolean(),
});
function toFile(fileLike: { name: string; contentType: string }) {
  return new File([], fileLike.name, { type: fileLike.contentType });
}
function AccountDocuments() {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { data: profile } = useData(
    'GET /customer/me/profile',
    {},
    { enabled: !!user?.id, staleTime: Infinity },
  );
  const updateCustomerMutation = useAction('PATCH /customer/me', {
    invalidate: ['GET /customer/me'],
    onSuccess: () =>
      toast.success(t('shared.actions.profile.documentsUpdated')),
  });

  const documentsSchema = z.object({
    bankCertificate: requiredFileSchema,
    commercialRegistrationFile: requiredFileSchema,
    taxCertificate: requiredFileSchema,
    zakaCertificate: requiredFileSchema,
    companyLogo: fileSchema,
    nationalAddressCertificate: requiredFileSchema,
  });

  const form = useForm({
    defaultValues: {
      bankCertificate: {
        file: profile?.companyDocuments.bankCertificate
          ? toFile(profile.companyDocuments.bankCertificate)
          : null,
        changed: false,
      },
      commercialRegistrationFile: {
        file: profile?.companyDocuments.commercialRegistrationFile
          ? toFile(profile.companyDocuments.commercialRegistrationFile)
          : null,
        changed: false,
      },
      taxCertificate: {
        file: profile?.companyDocuments.taxCertificate
          ? toFile(profile.companyDocuments.taxCertificate)
          : null,
        changed: false,
      },
      zakaCertificate: {
        file: profile?.companyDocuments.zakaCertificate
          ? toFile(profile.companyDocuments.zakaCertificate)
          : null,
        changed: false,
      },
      companyLogo: {
        file: profile?.companyDocuments.companyLogo
          ? toFile(profile.companyDocuments.companyLogo)
          : null,
        changed: false,
      },
      nationalAddressCertificate: {
        file: profile?.companyDocuments.nationalAddressCertificate
          ? toFile(profile.companyDocuments.nationalAddressCertificate)
          : null,
        changed: false,
      },
    },
    validators: {
      onSubmit: documentsSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          // Only upload files that were changed
          const fileUploads = await Promise.all([
            value.bankCertificate?.changed && value.bankCertificate?.file
              ? uploadFile(value.bankCertificate.file)
              : null,
            value.commercialRegistrationFile?.changed &&
            value.commercialRegistrationFile?.file
              ? uploadFile(value.commercialRegistrationFile.file)
              : null,
            value.taxCertificate?.changed && value.taxCertificate?.file
              ? uploadFile(value.taxCertificate.file)
              : null,
            value.zakaCertificate?.changed && value.zakaCertificate?.file
              ? uploadFile(value.zakaCertificate.file)
              : null,
            value.companyLogo?.changed && value.companyLogo?.file
              ? uploadFile(value.companyLogo.file)
              : null,
            value.nationalAddressCertificate?.changed &&
            value.nationalAddressCertificate?.file
              ? uploadFile(value.nationalAddressCertificate.file)
              : null,
          ]);

          const [
            bankCertificateId,
            commercialRegistrationId,
            taxCertificateId,
            zakaCertificateId,
            companyLogoId,
            nationalAddressCertificateId,
          ] = fileUploads;

          await updateCustomerMutation.mutateAsync({
            ...(bankCertificateId
              ? { bankCertificate: bankCertificateId }
              : {}),
            ...(commercialRegistrationId
              ? { commercialRegistrationFile: commercialRegistrationId }
              : {}),
            ...(taxCertificateId ? { taxCertificate: taxCertificateId } : {}),
            ...(zakaCertificateId
              ? { zakaCertificate: zakaCertificateId }
              : {}),
            ...(companyLogoId ? { companyLogo: companyLogoId } : {}),
            ...(nationalAddressCertificateId
              ? {
                  nationalAddressCertificate: nationalAddressCertificateId,
                }
              : {}),
          });

          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <div className="space-y-4">
      <CardContent className="px-0">
        <form
          className="space-y-6"
          onSubmit={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="bankCertificate"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  canDelete={false}
                  required
                  label={t('documents.bankCertificate')}
                  name={field.name}
                  value={field.state.value?.file || null}
                  onChange={(file) => {
                    field.handleChange({ file, changed: true });
                    field.handleBlur();
                  }}
                  suffix={
                    <DownloadFile
                      fileId={profile?.companyDocuments?.bankCertificate?.url}
                    />
                  }
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="commercialRegistrationFile"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  canDelete={false}
                  required
                  label={t('documents.commercialRegistration')}
                  name={field.name}
                  value={field.state.value?.file || null}
                  onChange={(file) => {
                    field.handleChange({ file, changed: true });
                    field.handleBlur();
                  }}
                  suffix={
                    <DownloadFile
                      fileId={
                        profile?.companyDocuments?.commercialRegistrationFile
                          ?.url
                      }
                    />
                  }
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="taxCertificate"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  canDelete={false}
                  required
                  label={t('documents.taxCertificate')}
                  name={field.name}
                  value={field.state.value?.file || null}
                  onChange={(file) => {
                    field.handleChange({ file, changed: true });
                    field.handleBlur();
                  }}
                  suffix={
                    <DownloadFile
                      fileId={profile?.companyDocuments?.taxCertificate?.url}
                    />
                  }
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="zakaCertificate"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  canDelete={false}
                  required
                  label={t('documents.zakaCertificate')}
                  name={field.name}
                  value={field.state.value?.file || null}
                  onChange={(file) => {
                    field.handleChange({ file, changed: true });
                    field.handleBlur();
                  }}
                  suffix={
                    <DownloadFile
                      fileId={profile?.companyDocuments?.zakaCertificate?.url}
                    />
                  }
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="companyLogo"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  canDelete={false}
                  label={t('documents.companyLogo')}
                  name={field.name}
                  value={field.state.value?.file || null}
                  onChange={(file) => {
                    field.handleChange({ file, changed: true });
                    field.handleBlur();
                  }}
                  suffix={
                    <DownloadFile
                      fileId={profile?.companyDocuments?.companyLogo?.url}
                    />
                  }
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="nationalAddressCertificate"
            children={(field) => (
              <div className="space-y-2">
                <UploadField
                  canDelete={false}
                  required
                  label={t('documents.nationalAddress')}
                  name={field.name}
                  value={field.state.value?.file || null}
                  onChange={(file) => {
                    field.handleChange({ file, changed: true });
                    field.handleBlur();
                  }}
                  suffix={
                    <DownloadFile
                      fileId={
                        profile?.companyDocuments?.nationalAddressCertificate
                          ?.url
                      }
                    />
                  }
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit || isSubmitting}
                className="mt-4 w-full cursor-pointer"
              >
                {isSubmitting ? t('common.saving') : t('common.save')}
              </Button>
            )}
          />
          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </div>
  );
}

function DownloadFile({ fileId }: { fileId?: string }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      asChild
      className="mr-auto h-6 shrink-0 px-2"
    >
      <a
        href={fileId}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <Download size={14} />
      </a>
    </Button>
  );
}
